import { createStyles } from '@mantine/core';

const SERIF = 'Georgia, "Times New Roman", Times, serif';

// Central style hook for the onboarding wizard. Colors resolve per color scheme so the
// whole flow supports dark mode; structural (non-color) properties are unchanged.
export const useOnboardingStyles = createStyles((theme) => {
  const dark = theme.colorScheme === 'dark';
  const c = {
    pageBg: dark ? '#0B0C0F' : '#FFFFFF',
    card: dark ? '#16181D' : '#ffffff',
    surface: dark ? '#1B1E25' : '#F9FAFB',
    surface2: dark ? '#1B1E25' : '#FAFAFA',
    border: dark ? '#2A2E37' : '#EAEAEA',
    border2: dark ? '#2A2E37' : '#E5E7EB',
    borderHover: dark ? '#4A4F5A' : '#D1D5DB',
    textTitle: dark ? '#F8FAFC' : '#0A0A0A',
    textPrimary: dark ? '#F3F4F6' : '#111827',
    textSecondary: dark ? '#B8BDC7' : '#4B5563',
    text3: dark ? '#C4C9D2' : '#374151',
    textGray: dark ? '#9BA1AC' : '#6B7280',
    textMuted: dark ? '#8A909B' : '#9CA3AF',
    // accents
    purple: dark ? '#A78BFA' : '#6D28D9',
    purpleText: dark ? '#C4B5FD' : '#7C3AED',
    purpleBg: dark ? '#231B34' : '#F6F4FF',
    purpleBg2: dark ? '#201830' : '#F5F3FF',
    purpleBorder: dark ? '#3B2E5C' : '#E9D5FF',
    purpleChip: dark ? '#2E2445' : '#EDE9FE',
    purpleDashed: dark ? '#4B3B7A' : '#C4B5FD',
    greenBg: dark ? '#0F241C' : '#ECFDF5',
    greenBorder: dark ? '#14532D' : '#A7F3D0',
    greenText: dark ? '#34D399' : '#059669',
    amberBg: dark ? '#2A2410' : '#FFFBEB',
    amberBorder: dark ? '#4D3B12' : '#FDE68A',
    redBg: dark ? '#2A1414' : '#FEF2F2',
    redBorder: dark ? '#5C1E1E' : '#FECACA',
    // inverted primary button (black in light, near-white in dark)
    invBtnBg: dark ? '#F3F4F6' : '#111827',
    invBtnText: dark ? '#111827' : '#ffffff',
    invBtnHover: dark ? '#ffffff' : '#000000',
    invBtnDisBg: dark ? '#242830' : '#F3F4F6',
    invBtnDisText: dark ? '#5A606B' : '#9CA3AF',
    chip: dark ? '#1B1E25' : '#ffffff',
    markBg: dark ? '#2A2E37' : '#111827',
    logoHover: dark ? '#A78BFA' : '#111827',
  };
  return {
  root: {
    minHeight: '100vh',
    background: c.pageBg,
    display: 'flex',
    flexDirection: 'column',
    padding: 'clamp(14px, 2.5vw, 26px)',
  },

  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    maxWidth: 1180,
    margin: '0 auto',
    padding: '2px 2px 8px',
  },

  topLogo: {
    height: 30,
    width: 'auto',
    objectFit: 'contain',
  },

  needHelpBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 7,
    background: c.chip,
    border: `1px solid ${c.border2}`,
    borderRadius: 999,
    padding: '7px 14px',
    fontSize: 14,
    fontWeight: 600,
    color: c.textPrimary,
    cursor: 'pointer',
    transition: 'background 0.15s ease',
    '&:hover': { background: c.surface2 },
  },

  // ---- Progress tracker (dots only) ----
  tracker: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 0,
    padding: '10px 0 22px',
    maxWidth: 560,
    margin: '0 auto',
    width: '100%',
  },

  bubble: {
    width: 30,
    height: 30,
    borderRadius: '50%',
    display: 'grid',
    placeItems: 'center',
    fontWeight: 600,
    fontSize: 13,
    border: `2px solid ${c.border2}`,
    background: c.chip,
    color: c.textMuted,
    transition: 'all 0.3s ease',
    flex: '0 0 auto',
  },

  bubbleActive: {
    borderColor: c.invBtnBg,
    color: c.invBtnText,
    background: c.invBtnBg,
  },

  bubbleDone: {
    borderColor: c.invBtnBg,
    background: c.invBtnBg,
    color: c.invBtnText,
  },

  trackerLine: {
    height: 2,
    width: 64,
    background: c.border2,
    position: 'relative',
    overflow: 'hidden',
  },

  trackerLineFill: {
    position: 'absolute',
    inset: 0,
    background: c.invBtnBg,
    transformOrigin: 'left',
    transition: 'transform 0.45s ease',
  },

  // ---- Main card ----
  card: {
    flex: 1,
    background: c.card,
    borderRadius: 22,
    border: `1px solid ${c.border}`,
    boxShadow: dark ? '0 1px 2px rgba(0,0,0,0.4)' : '0 1px 2px rgba(16,24,40,0.03)',
    width: '100%',
    maxWidth: 1180,
    margin: '0 auto',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 'clamp(28px, 5vw, 64px)',
    overflow: 'hidden',
  },

  // constrains the centered content column
  inner: {
    width: '100%',
    maxWidth: 620,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
  },

  mark: {
    width: 56,
    height: 56,
    borderRadius: 14,
    display: 'grid',
    placeItems: 'center',
    background: c.markBg,
    color: '#fff',
    marginBottom: 22,
  },

  title: {
    fontFamily: SERIF,
    fontSize: 'clamp(30px, 4.5vw, 46px)',
    fontWeight: 500,
    letterSpacing: '-0.01em',
    color: c.textTitle,
    lineHeight: 1.1,
  },

  subtitle: {
    fontSize: 'clamp(15px, 1.6vw, 18px)',
    fontWeight: 600,
    color: c.textPrimary,
    marginTop: 12,
  },

  optionalTag: {
    fontFamily: 'inherit',
    fontSize: '0.5em',
    fontWeight: 400,
    color: c.textMuted,
    verticalAlign: 'middle',
  },

  fields: {
    width: '100%',
    marginTop: 30,
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
    textAlign: 'left',
  },

  fieldLabel: {
    color: c.textPrimary,
    fontSize: 15.5,
    marginBottom: 6,
  },

  tipText: {
    width: '100%',
    textAlign: 'left',
    fontSize: 13.5,
    color: c.textGray,
    marginTop: 2,
    lineHeight: 1.5,
  },

  tipLabel: {
    color: c.purpleText,
    fontWeight: 700,
  },

  // ---- Reusable step video card ----
  videoCard: {
    display: 'flex',
    alignItems: 'center',
    gap: 14,
    width: '100%',
    maxWidth: 440,
    margin: '18px auto 4px',
    padding: 10,
    background: c.card,
    border: `1px solid ${c.border}`,
    borderRadius: 14,
    cursor: 'pointer',
    textAlign: 'left',
    transition: 'all 0.15s ease',
    '&:hover': { borderColor: c.borderHover, boxShadow: '0 6px 16px -10px rgba(16,24,40,0.25)' },
  },

  videoThumb: {
    position: 'relative',
    flexShrink: 0,
    width: 132,
    height: 74,
    borderRadius: 10,
    overflow: 'hidden',
    background: 'radial-gradient(120% 120% at 70% 20%, #4a3d8f 0%, transparent 55%), linear-gradient(135deg, #2a2440 0%, #1a1626 100%)',
    display: 'grid',
    placeItems: 'center',
  },

  videoBars: {
    position: 'absolute',
    left: 12,
    top: 13,
    right: 12,
    display: 'grid',
    gap: 6,
    opacity: 0.45,
  },

  videoBar: {
    height: 6,
    borderRadius: 4,
    background: 'rgba(255,255,255,0.6)',
  },

  videoPlay: {
    position: 'relative',
    width: 34,
    height: 34,
    borderRadius: '50%',
    background: '#fff',
    display: 'grid',
    placeItems: 'center',
    color: '#5B21B6',
    boxShadow: '0 4px 10px rgba(0,0,0,0.25)',
  },

  videoDuration: {
    position: 'absolute',
    right: 6,
    bottom: 6,
    fontSize: 11,
    fontWeight: 600,
    color: '#fff',
    background: 'rgba(0,0,0,0.55)',
    borderRadius: 6,
    padding: '1px 6px',
  },

  videoTitle: {
    fontSize: 15,
    fontWeight: 700,
    color: c.textPrimary,
    lineHeight: 1.2,
  },

  videoSub: {
    fontSize: 13,
    color: c.textGray,
    marginTop: 3,
  },

  // ---- large hero video (final step) ----
  heroVideo: {
    position: 'relative',
    width: '100%',
    borderRadius: 18,
    overflow: 'hidden',
    aspectRatio: '16 / 10',
    cursor: 'pointer',
    background:
      'radial-gradient(120% 120% at 65% 15%, #5b3fb0 0%, transparent 55%), linear-gradient(135deg, #2a2440 0%, #17132a 100%)',
    border: `1px solid ${c.border}`,
  },

  heroBadge: {
    position: 'absolute',
    top: 14,
    right: 14,
    background: 'rgba(0,0,0,0.55)',
    color: '#fff',
    fontSize: 12.5,
    fontWeight: 600,
    borderRadius: 999,
    padding: '5px 12px',
  },

  heroPlay: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 76,
    height: 76,
    borderRadius: '50%',
    background: '#fff',
    display: 'grid',
    placeItems: 'center',
    color: '#111827',
    boxShadow: '0 12px 30px rgba(0,0,0,0.35)',
  },

  heroOverlay: {
    position: 'absolute',
    left: 20,
    bottom: 18,
    right: 20,
    textAlign: 'left',
  },

  heroTitle: {
    color: '#fff',
    fontSize: 18,
    fontWeight: 700,
  },

  heroSub: {
    color: 'rgba(255,255,255,0.8)',
    fontSize: 13.5,
    marginTop: 2,
  },

  // ---- final step: where to go ----
  subheading: {
    fontFamily: 'Georgia, "Times New Roman", serif',
    fontSize: 26,
    fontWeight: 500,
    color: c.textTitle,
    textAlign: 'center',
  },

  helpPill: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    background: c.chip,
    border: `1px solid ${c.border2}`,
    borderRadius: 999,
    padding: '9px 16px',
    fontSize: 14,
    fontWeight: 600,
    color: c.textPrimary,
    cursor: 'pointer',
    textDecoration: 'none',
    transition: 'background 0.15s ease',
    '&:hover': { background: c.surface2 },
  },

  videoFrame: {
    position: 'relative',
    width: '100%',
    paddingTop: '56.25%',
    background: '#1E1B2E',
  },

  videoPlayer: {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    background: '#1E1B2E',
    border: 'none',
  },

  // ---- Step 2: auto-detect / verify / notices ----
  detectCard: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    border: `1px solid ${c.purpleBorder}`,
    background: c.purpleBg,
    borderRadius: 12,
    padding: 14,
    textAlign: 'left',
  },

  detectIcon: {
    width: 40,
    height: 40,
    borderRadius: 10,
    display: 'grid',
    placeItems: 'center',
    background: c.purpleChip,
    color: c.purple,
    flexShrink: 0,
  },

  stepNote: {
    width: '100%',
    border: `1px solid ${c.border2}`,
    background: c.surface,
    borderRadius: 12,
    padding: '14px 16px',
    textAlign: 'left',
  },

  noteHeading: {
    fontSize: 13,
    fontWeight: 700,
    color: c.textPrimary,
    marginBottom: 4,
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  },

  specialBox: {
    width: '100%',
    border: `1px solid ${c.purpleBorder}`,
    background: c.purpleBg,
    borderRadius: 12,
    padding: '14px 16px',
    textAlign: 'left',
  },

  cachingBox: {
    width: '100%',
    border: `1px solid ${c.amberBorder}`,
    background: c.amberBg,
    borderRadius: 12,
    padding: '14px 16px',
    textAlign: 'left',
  },

  failBox: {
    width: '100%',
    border: `1px solid ${c.redBorder}`,
    background: c.redBg,
    borderRadius: 12,
    padding: '14px 16px',
    textAlign: 'left',
  },

  bypassBox: {
    width: '100%',
    border: `1px dashed ${c.purpleDashed}`,
    background: c.purpleBg,
    borderRadius: 12,
    padding: '14px 16px',
    textAlign: 'left',
  },

  bulletRow: {
    display: 'flex',
    gap: 8,
    alignItems: 'flex-start',
    fontSize: 13.5,
    color: c.textSecondary,
    lineHeight: 1.5,
    marginTop: 5,
  },

  linkBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    fontSize: 13.5,
    fontWeight: 600,
    color: c.textPrimary,
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: 0,
    '&:hover': { textDecoration: 'underline' },
  },

  verifyList: {
    width: '100%',
    display: 'grid',
    gap: 12,
    border: `1px solid ${c.border}`,
    borderRadius: 12,
    padding: '16px 18px',
    textAlign: 'left',
  },

  verifyItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 11,
    fontSize: 14,
    fontWeight: 600,
    transition: 'color 0.2s ease',
  },

  verifyPending: {
    width: 18,
    height: 18,
    borderRadius: '50%',
    border: `2px solid ${c.border2}`,
    flexShrink: 0,
  },

  // ---- Step 3: related-domains info boxes ----
  qaBox: {
    width: '100%',
    border: `1px solid ${c.purpleBorder}`,
    background: c.purpleBg,
    borderRadius: 14,
    padding: '16px 18px',
    textAlign: 'left',
  },

  qaHead: {
    display: 'flex',
    alignItems: 'center',
    gap: 9,
    fontSize: 15,
    fontWeight: 700,
    color: c.textPrimary,
    marginBottom: 8,
  },

  qaDot: {
    width: 22,
    height: 22,
    borderRadius: '50%',
    background: '#7C3AED',
    color: '#fff',
    display: 'grid',
    placeItems: 'center',
    flexShrink: 0,
  },

  qaText: {
    fontSize: 14,
    color: c.textSecondary,
    lineHeight: 1.55,
  },

  qaSubhead: {
    fontSize: 13.5,
    fontWeight: 700,
    color: c.text3,
    margin: '12px 0 6px',
  },

  qaList: {
    display: 'grid',
    gap: 6,
    paddingLeft: 2,
  },

  qaItem: {
    display: 'flex',
    gap: 10,
    alignItems: 'center',
    fontSize: 14,
    color: c.text3,
  },

  qaBullet: {
    width: 5,
    height: 5,
    borderRadius: '50%',
    background: '#7C3AED',
    flexShrink: 0,
  },

  qaNote: {
    fontSize: 13.5,
    color: c.textMuted,
    marginTop: 14,
    lineHeight: 1.5,
  },

  discBox: {
    width: '100%',
    border: `1px solid ${c.border}`,
    borderRadius: 12,
    overflow: 'hidden',
    textAlign: 'left',
  },

  // ---- Step 4: funnel builder + info panels ----
  panel: {
    width: '100%',
    border: `1px solid ${c.border}`,
    background: c.card,
    borderRadius: 14,
    padding: '16px 18px',
    textAlign: 'left',
  },

  infoTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    fontSize: 15,
    fontWeight: 700,
    color: c.textPrimary,
    marginBottom: 6,
  },

  showToggle: {
    background: 'none',
    border: 'none',
    color: c.purpleText,
    fontWeight: 600,
    fontSize: 13.5,
    cursor: 'pointer',
    padding: 0,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    marginTop: 10,
  },

  fieldHelp: {
    fontSize: 13,
    color: c.textMuted,
    margin: '2px 0 8px',
    textAlign: 'left',
    lineHeight: 1.45,
  },

  sectionLabel: {
    fontSize: 15.5,
    fontWeight: 700,
    color: c.textPrimary,
    textAlign: 'left',
    marginTop: 6,
  },

  checkGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '9px 22px',
    marginTop: 10,
  },

  checkItem: {
    display: 'flex',
    gap: 9,
    alignItems: 'center',
    fontSize: 14,
    fontWeight: 600,
    color: c.textPrimary,
  },

  exGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '7px 24px',
    marginTop: 10,
  },

  // journey builder
  journeyWrap: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 0,
    marginTop: 6,
  },

  journeyCard: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    gap: 14,
    border: `1px solid ${c.border2}`,
    borderRadius: 12,
    padding: '13px 16px',
    background: c.card,
  },

  journeyCardStart: {
    borderColor: c.purpleDashed,
    background: c.purpleBg,
  },

  stepNum: {
    width: 34,
    height: 34,
    borderRadius: 8,
    background: '#6D28D9',
    color: '#fff',
    fontWeight: 700,
    fontSize: 15,
    display: 'grid',
    placeItems: 'center',
    flexShrink: 0,
  },

  stepEyebrow: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: c.purpleText,
  },

  startBadge: {
    background: c.purpleChip,
    color: c.purple,
    fontWeight: 700,
    fontSize: 12,
    borderRadius: 999,
    padding: '4px 12px',
    flexShrink: 0,
  },

  addPageBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    border: `1.5px dashed ${c.purpleDashed}`,
    color: c.purple,
    background: c.card,
    borderRadius: 10,
    padding: '10px 22px',
    fontWeight: 700,
    fontSize: 14,
    cursor: 'pointer',
    transition: 'background 0.15s ease',
    '&:hover': { background: c.purpleBg },
  },

  // black primary button used inside cards and bottom nav (inverts in dark)
  blackBtn: {
    background: c.invBtnBg,
    color: c.invBtnText,
    border: `1px solid ${c.invBtnBg}`,
    height: 52,
    borderRadius: 12,
    fontSize: 15,
    fontWeight: 600,
    '&:hover': { background: c.invBtnHover },
    '&:disabled': {
      background: c.invBtnDisBg,
      borderColor: c.invBtnDisBg,
      color: c.invBtnDisText,
    },
  },

  // ---- bottom navigation ----
  bottomNav: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    maxWidth: 1180,
    width: '100%',
    margin: '18px auto 4px',
  },

  navBack: {
    background: c.chip,
    color: c.textPrimary,
    border: `1px solid ${c.border2}`,
    borderRadius: 12,
    height: 48,
    padding: '0 20px',
    fontWeight: 600,
    fontSize: 15,
    '&:hover': { background: c.surface2 },
    '&:disabled': { color: c.textMuted, borderColor: c.border, background: c.chip },
  },

  navNext: {
    background: c.invBtnBg,
    color: c.invBtnText,
    border: `1px solid ${c.invBtnBg}`,
    borderRadius: 12,
    height: 48,
    padding: '0 22px',
    fontWeight: 600,
    fontSize: 15,
    '&:hover': { background: c.invBtnHover },
    '&:disabled': {
      background: c.invBtnDisBg,
      borderColor: c.invBtnDisBg,
      color: c.invBtnDisText,
    },
  },

  verifyOk: {
    width: '100%',
    background: c.greenBg,
    border: `1px solid ${c.greenBorder}`,
    borderRadius: 12,
    padding: '12px 14px',
  },

  hintBox: {
    width: '100%',
    textAlign: 'left',
    background: c.surface2,
    border: `1px solid ${c.border}`,
    borderRadius: 12,
    padding: '12px 14px',
    fontSize: 13.5,
    color: c.textSecondary,
    lineHeight: 1.5,
  },

  // ---- Webhook logo cards ----
  groupTitle: {
    textAlign: 'left',
    fontSize: 13,
    fontWeight: 700,
    letterSpacing: '0.02em',
    color: c.textGray,
    marginBottom: 10,
  },

  logoGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(108px, 1fr))',
    gap: 12,
  },

  logoCard: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 9,
    background: c.card,
    border: `1.5px solid ${c.border2}`,
    borderRadius: 14,
    padding: '16px 10px 12px',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
    '&:hover': { borderColor: c.logoHover, transform: 'translateY(-2px)' },
  },

  logoCardActive: {
    borderColor: '#34D399',
    background: c.greenBg,
    boxShadow: '0 8px 20px -12px rgba(16,185,129,0.35)',
  },

  logoImg: {
    height: 40,
    width: 40,
    objectFit: 'contain',
  },

  logoLabel: {
    fontSize: 12,
    fontWeight: 600,
    color: c.text3,
    textAlign: 'center',
    lineHeight: 1.25,
  },

  logoCheck: {
    position: 'absolute',
    top: 7,
    right: 7,
    width: 18,
    height: 18,
    borderRadius: '50%',
    background: '#10B981',
    color: '#fff',
    display: 'grid',
    placeItems: 'center',
  },

  // custom-integration tile (no logo)
  customTile: {
    width: 40,
    height: 40,
    borderRadius: 10,
    display: 'grid',
    placeItems: 'center',
    background: '#7C3AED',
    color: '#fff',
  },

  selectedLine: {
    textAlign: 'center',
    fontSize: 14,
    color: c.text3,
    marginTop: 4,
  },

  selectedName: {
    color: c.purple,
    fontWeight: 700,
  },

  centerHeading: {
    fontSize: 19,
    fontWeight: 700,
    color: c.textPrimary,
    textAlign: 'center',
  },

  // ---- guided setup wizard ----
  gsPills: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: 8,
    marginTop: 8,
  },

  gsPill: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    borderRadius: 999,
    border: `1px solid ${c.border2}`,
    background: c.chip,
    color: c.textGray,
    fontSize: 13.5,
    fontWeight: 600,
    padding: '6px 14px',
  },

  gsPillActive: {
    borderColor: c.purpleBorder,
    background: c.purpleBg2,
    color: c.purple,
  },

  gsPillDone: {
    borderColor: c.greenBorder,
    background: c.greenBg,
    color: c.greenText,
  },

  gsDots: {
    display: 'flex',
    justifyContent: 'center',
    gap: 7,
    margin: '10px 0',
  },

  gsDot: {
    width: 8,
    height: 8,
    borderRadius: 999,
    background: c.border2,
    transition: 'all 0.2s ease',
  },

  gsDotActive: {
    width: 22,
    background: '#7C3AED',
  },

  gsDotDone: {
    background: c.purpleDashed,
  },


  gsUrlBanner: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    background: c.purpleBg2,
    border: `1px solid ${c.purpleBorder}`,
    borderRadius: 12,
    padding: '12px 14px',
  },

  gsUrlText: {
    fontFamily: 'ui-monospace, Menlo, monospace',
    fontSize: 13,
    color: c.textPrimary,
    wordBreak: 'break-all',
  },

  gsPurpleBtn: {
    background: '#6D28D9',
    color: '#fff',
    border: 'none',
    borderRadius: 12,
    height: 50,
    padding: '0 26px',
    fontSize: 15,
    fontWeight: 700,
    '&:hover': { background: '#5B21B6' },
  },

  estPill: {
    background: c.purpleBg2,
    color: c.purple,
    fontWeight: 700,
    fontSize: 13.5,
    borderRadius: 12,
    padding: '12px 16px',
    textAlign: 'center',
  },

  helpPanel: {
    position: 'fixed',
    right: 22,
    top: 62,
    width: 300,
    maxWidth: 'calc(100vw - 32px)',
    zIndex: 41,
    background: c.card,
    borderRadius: 16,
    border: `1px solid ${c.border}`,
    boxShadow: '0 24px 48px -18px rgba(16,24,40,0.28)',
    padding: 16,
  },

  helpLink: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '10px 10px',
    borderRadius: 10,
    textDecoration: 'none',
    color: c.textPrimary,
    fontSize: 14,
    fontWeight: 500,
    transition: 'background 0.15s ease',
    '&:hover': { background: c.surface2 },
  },

  };
});
